import { MeetingSource } from "@kalyzee/kast-web-sdk";
import { AudioMixerOptions } from "@kalyzee/kast-web-sdk/web-components";
import objectAssignDeep from "object-assign-deep";
import { MeetingSourceExtended } from "../hooks/meeting";
import { getEnvSettings, initEnv } from "./env";
import { appMessage } from "./message";
// Deep import (not the barrel): settings must not drag React components in.
import { getGlobalQuery, NavigateGlobalQuery } from "./navigation/queries";
import {
  getSelectedSettingsKey,
  getSettings,
  getSettingsByKey,
  listSettingsKeys,
  removeSettingsByKey,
  SETTINGS_DEFAULT_KEY,
  SETTINGS_KEY_PREFIX,
  setSelectedSettingsKey,
  setSettingsByKey,
} from "./storage";
import { SecondaryScreenLayout } from "../interfaces/secondaryScreens";
import { GridMode, LayoutType } from "../interfaces/ui";

export interface SettingsSecret {
  adminPin?: string;
  localPassword?: string | null;
  remotePassword?: string | null;
}

interface AudioVideo<T> {
  audio?: T,
  video?: T,
}

export interface SettingsDebug {
  displaySessionIds?: boolean;
  displayIncomingStreamStats?: boolean;
  displayOutgoingStreamStats?: boolean;
  audio?: {
    mixer?: AudioMixerOptions & { public?: boolean };
  };
  sdkClient?: {
    public?: boolean;
  };
  meetingContext?: {
    public?: boolean;
  };
  maxBitrate?: {
    pedagogy?: {
      webcam?: AudioVideo<number>;
      screensharing?: AudioVideo<number>;
    },
    student?: {
      webcam?: AudioVideo<number>;
      screensharing?: AudioVideo<number>;
    },
    expert?: {
      camera?: AudioVideo<number>;
      screen?: AudioVideo<number>;
      webcam?: AudioVideo<number>;
    },
  }
}

export interface SettingsMessagingBridgeMaster {
  enableBroadcast?: boolean;
}

export interface SettingsMessagingBridgeSlave {
  enableBroadcast?: boolean;
  enableWebsocket?: boolean;
  websocketEndpoint?: string;
}

export interface SettingsLogs {
  main?: boolean;
  /** Passive camera/mic diagnostics (see helpers/mediaDiagnostics.ts). */
  mediaDiagnostics?: boolean;
  meetingContext?: boolean;
  ovenMediaSession?: {
    reception?: {
      main?: boolean,
      webrtc?: boolean,
    },
    emission?: {
      main?: boolean,
      webrtc?: boolean,
    },
  };
}

export interface Settings {
  secret?: SettingsSecret;
  debug?: SettingsDebug;
  roomId?: string;
  bridge?: {
    master?: SettingsMessagingBridgeMaster;
    slave?: SettingsMessagingBridgeSlave;
  };
  sources?: Partial<MeetingSourceExtended>[];
  videoFeedback?: {
    source: MeetingSource;
  };
  webcamFeedback?: {
    source: Partial<MeetingSourceExtended>;
  };
  emission?: {
    sdpFormatter?: {
      offer?: string,
      answer?: string,
    }
  }
  dev?: boolean;
  logs?: SettingsLogs;
  soundIndicators?: boolean;
  carousel?: {
    prelaodDelay?: number,
    interval?: number,
    layouts?: SecondaryScreenLayout[],
    filters?: {
      carouselId: string,
      /**
       * Complex match string, applied to a context composed with :
       * - session
       * - media
       * - currentSession
       */
      value: string,
    }[],
  },
  ui?: {
    hideLogoutButton?: boolean;
    showExpectedRoleBadge?: boolean;
    showFullscreenButton?: boolean;
    /**
     * LMS/iframe integration mode: /list becomes a "join a meeting" screen and
     * leaving a meeting always exits to its lobby (never back to the list).
     */
    disableMeetingList?: boolean;
    /**
     * Disable clamping of future server timestamps for elapsed counters. When a
     * client clock lags behind the server, `startedAt`/`pausedAt` can appear to
     * be in the future and counters would show 00:00:00 until the clock catches
     * up. By default the reference is clamped to "now" so counters start
     * immediately; set this to keep the legacy frozen-at-0 behavior.
     */
    disableTimeClamping?: boolean;
    grid?: {
      /** Grid disposition for the `Full` layout. Default: adaptive. */
      mode?: GridMode;
      /** Players per page. Leave unset (or 0) for automatic (screen-based). */
      pageSize?: number;
      /** Minimum readable tile short-side (CSS px) used in automatic mode. Default: 180. */
      autoTileMinPx?: number;
    };
  },
  features?: {
    survey?: boolean;
    board?: boolean;
    boardMatch?: string;
    audioMatch?: string;
    highlightAutoAcceptTimer?: number; // seconds before auto-accepting highlight invitation (undefined = disabled)
    rememberGuests?: boolean; // persist guest identities per meeting for later reuse (absent = disabled)
  },
}

// ---------------------------------------------------------------------------
// Settings are resolved from 3 layers, lowest to highest priority:
//   1. env    — SETTINGS env var (full Settings JSON: Docker / VITE_SETTINGS)
//   2. url    — transient `?settings=` overlay, never persisted
//   3. stored — localStorage, the only layer writes go to
// ---------------------------------------------------------------------------

interface SettingsLayers {
  env: Partial<Settings>;
  url: Partial<Settings>;
  stored: Settings;
}

let layers: SettingsLayers | undefined;
let resolved: Settings = {};
const listeners = new Set<() => void>();

/** `logs` was a plain boolean ("everything on/off") before schema v10 — normalize legacy data. */
const normalizeSettings = (settings: Partial<Settings>): Partial<Settings> => {
  const logs = settings.logs as SettingsLogs | boolean | undefined;
  if (typeof logs === "boolean") {
    settings.logs = logs
      ? {
          main: true,
          mediaDiagnostics: true,
          meetingContext: true,
          ovenMediaSession: { reception: { main: true, webrtc: true }, emission: { main: true, webrtc: true } },
        }
      : {};
  }
  return settings;
};

const parseSettingsObject = (str: string): Partial<Settings> | undefined => {
  try {
    const json = JSON.parse(str);
    if (json && typeof json === "object" && !Array.isArray(json)) return normalizeSettings(json);
  } catch (err) {}
  return undefined;
};

const readEnvLayer = (): Partial<Settings> => {
  initEnv(); // idempotent — settings may be read before SdkContextProvider mounts
  const str = getEnvSettings();
  return (str && parseSettingsObject(str)) || {};
};

// ---- Multi-config: configs live under `settings-<slug>` keys; the active one
// is pointed to by `selected-settings-key` (fallback: the default config,
// stored under `settings-default` or the legacy `settings` key). ----

/** Key of the active config — falls back to the default when the pointer is absent or dangling. */
export const resolveActiveSettingsKey = (): string => {
  const selected = getSelectedSettingsKey();
  if (selected && getSettingsByKey(selected) !== null) return selected;
  return SETTINGS_DEFAULT_KEY;
};

const readConfigData = (key: string): Settings => {
  try {
    let str = getSettingsByKey(key);
    if (str === null && key === SETTINGS_DEFAULT_KEY) str = getSettings(); // legacy `settings` key
    if (str) return normalizeSettings(JSON.parse(str));
  } catch (err) {}
  return {};
};

const writeConfigData = (key: string, settings: Settings): void => {
  setSettingsByKey(key, JSON.stringify(settings));
};

const readStoredLayer = (): Settings => readConfigData(resolveActiveSettingsKey());

const resolve = (l: SettingsLayers): Settings => objectAssignDeep({}, l.env, l.url, l.stored);

const ensureLayers = (): SettingsLayers => {
  if (!layers) {
    layers = { env: readEnvLayer(), url: {}, stored: readStoredLayer() };
    resolved = resolve(layers);
  }
  return layers;
};

const notify = () => {
  resolved = resolve(ensureLayers());
  listeners.forEach((cb) => cb());
};

/** 3-layer merged view (env < url < stored). Stable reference between updates. */
export const getResolvedSettings = (): Settings => {
  ensureLayers();
  return resolved;
};

/** Raw localStorage layer — what the settings form and the host bridge read/write. */
export const getStoredSettings = (): Settings => ensureLayers().stored;

export const subscribeSettings = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
};

export const updateStoredSettings = (partial: Partial<Settings>, options?: { merge?: boolean, persist?: boolean }): Settings => {
  const l = ensureLayers();
  // Merge against the stored layer only: merging against the resolved view
  // would bake transient env/URL values into localStorage.
  const next: Settings = options?.merge
    ? objectAssignDeep({}, l.stored, partial)
    : objectAssignDeep({}, partial);
  l.stored = next;
  if (options?.persist) {
    try {
      writeConfigData(resolveActiveSettingsKey(), next);
      appMessage.postSettings(next);
    } catch (err) {}
  }
  notify();
  return next;
};

// ---------------------------------------------------------------------------
// Config management (multi-config)
// ---------------------------------------------------------------------------

export interface SettingsConfigInfo {
  key: string;
  name: string;
  selected: boolean;
}

/** "Salle Averroès !" -> "salle-averroes" */
export const slugifySettingsConfigName = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // strip accents
    .replace(/[^a-z0-9\s_-]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** Every stored config (default first), flagged with the active one. */
export const listSettingsConfigs = (): SettingsConfigInfo[] => {
  const active = resolveActiveSettingsKey();
  const keys = new Set(listSettingsKeys());
  keys.add(SETTINGS_DEFAULT_KEY); // always exists, possibly only as the legacy key
  return [...keys]
    .sort((a, b) => {
      if (a === SETTINGS_DEFAULT_KEY) return -1;
      if (b === SETTINGS_DEFAULT_KEY) return 1;
      return a.localeCompare(b);
    })
    .map((key) => ({ key, name: key.slice(SETTINGS_KEY_PREFIX.length), selected: key === active }));
};

export const getSettingsConfigData = (key: string): Settings => readConfigData(key);

/** Persist a config; if it is the active one, the app picks it up immediately. */
export const saveSettingsConfigData = (key: string, settings: Settings): void => {
  writeConfigData(key, settings);
  if (key === resolveActiveSettingsKey()) {
    ensureLayers().stored = settings;
    try { appMessage.postSettings(settings); } catch (err) {}
    notify();
  }
};

/** Create a config from a display name (throws if invalid or already taken). */
export const createSettingsConfig = (name: string, data: Settings): SettingsConfigInfo => {
  const slug = slugifySettingsConfigName(name);
  if (!slug) throw new Error("Nom invalide : il doit contenir au moins un caractère alphanumérique");
  const key = SETTINGS_KEY_PREFIX + slug;
  if (key === SETTINGS_DEFAULT_KEY || getSettingsByKey(key) !== null) {
    throw new Error(`Une configuration « ${slug} » existe déjà`);
  }
  writeConfigData(key, data);
  return { key, name: slug, selected: false };
};

/**
 * Delete a config; if it was the active one, the default takes over.
 * The default cannot be deleted (it is the resolution fallback) — empty it instead.
 */
export const deleteSettingsConfig = (key: string): void => {
  if (key === SETTINGS_DEFAULT_KEY) throw new Error("La configuration par défaut ne peut pas être supprimée");
  const wasActive = key === resolveActiveSettingsKey();
  removeSettingsByKey(key);
  if (wasActive) selectSettingsConfig(SETTINGS_DEFAULT_KEY);
};

/** Make a config the active one (the app switches to it immediately). */
export const selectSettingsConfig = (key: string): void => {
  setSelectedSettingsKey(key);
  const stored = readStoredLayer();
  ensureLayers().stored = stored;
  try { appMessage.postSettings(stored); } catch (err) {}
  notify();
};

export const encodeBase64Url = (str: string): string => {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/** Decodes base64url (or plain base64) to text. Returns undefined if invalid. */
export const decodeBase64Url = (str: string): string | undefined => {
  try {
    const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return new TextDecoder().decode(Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)));
  } catch (err) {
    return undefined;
  }
};

/**
 * `?settings=` payload: raw JSON or base64url-encoded JSON. base64url is the
 * preferred form — opaque, so `"`/`&`/`#` never break the query string.
 */
export const parseSettingsPayload = (payload: string): Partial<Settings> | undefined => {
  const json = parseSettingsObject(payload);
  if (json) return json;
  const decoded = decodeBase64Url(payload);
  if (decoded !== undefined) return parseSettingsObject(decoded);
  return undefined;
};

let lastUrlPayload: string | null | undefined; // undefined = never synced

export const syncSettingsUrlOverlay = (href: string): void => {
  const payload = getGlobalQuery(href, NavigateGlobalQuery.SETTINGS) ?? null;
  if (payload === lastUrlPayload) return; // one parse shared by every useSettings instance
  lastUrlPayload = payload;
  let overlay: Partial<Settings> = {};
  if (payload) {
    const parsed = parseSettingsPayload(payload);
    if (parsed) overlay = parsed;
    else console.warn("[settings] Invalid ?settings payload (expected JSON or base64url JSON) — ignored");
  }
  ensureLayers().url = overlay;
  notify();
};
